// TODO: remove ts-nocheck and fix warnings and errors on this file
// @ts-nocheck
import Link from 'next/link';
import PortableText from '@sanity/block-content-to-react';
import {
  Box,
  Heading,
  Icon,
  Link as ChakraLink,
  ListItem,
  OrderedList,
  UnorderedList,
  Text,
  AspectRatio
} from '@chakra-ui/react';
import YouTube from 'react-youtube';
import getYouTubeId from 'get-youtube-id';
import { LinkIcon } from '@heroicons/react/outline';
import { normalizeString } from '@utils';
import { ExternalLinkIcon } from '@components/common/icons';
import { Attribution } from './attribution';
import { Callout } from './callout';
import { CodeSnippet } from './code-snippet';
import { Code } from './code';
import { EnvSnippet } from './env-snippet';
import { Image } from './image';
import { Markdown } from './mdx';
import { PropsTable } from './props-table';
import { Table } from './table';
import { Tabs } from './tabs';
import { tableStyle } from './table-style';
const { toKebabCase } = normalizeString;

export const types = {
  block: props => {
    const { node, children } = props;
    const { style } = node;

    if (/^h\d/.test(style)) {
      const HeadingTag = style;
      // Create ID based off heading text content
      // Sometimes there is a nested <span> inside
      const headingId =
        typeof children[0] === 'string'
          ? toKebabCase(children[0])
          : toKebabCase(children[0].props.node.children[0]);

      const size = `docs${HeadingTag}`;
      const marginY = HeadingTag === 'h2' ? 8 : 6;

      return (
        <Heading
          as={HeadingTag}
          id={headingId}
          size={size}
          sx={{
            marginTop: '-75px',
            paddingTop: `calc(100px + var(--chakra-space-${marginY}))`,
            paddingBottom: `var(--chakra-space-${marginY})`
          }}
          role="group"
        >
          <span>{children}</span>
          <a href={`#${headingId}`} aria-hidden="true" tabIndex={-1}>
            <Icon
              as={LinkIcon}
              boxSize={4}
              display="none"
              color="gray.500"
              ml={2}
              _groupHover={{
                display: 'inline-block'
              }}
            />
          </a>
        </Heading>
      );
    }

    // Return default serializer
    return (
      <Box my={4} lineHeight={1.8}>
        {PortableText.defaultSerializers.types.block(props)}
      </Box>
    );
  },
  code: props => (
    <Code highlightedLines={props.node.highlightedLines} theme="dark">
      {props.node.code}
    </Code>
  ),
  codeSnippet: ({ node }) => {
    return (
      <CodeSnippet
        body={node.codeBody}
        theme={node.theme}
        withCopy={node.copyButton}
        withLineNumbers={node.lineNumbers}
      />
    );
  },
  envSnippet: ({ node }) => {
    return (
      <EnvSnippet
        body={node.codeBody}
        theme={node.theme}
        withCopy={node.copyButton}
        withLineNumbers={node.lineNumbers}
      />
    );
  },
  image: props => <Image {...props} alt={props.node.alt} />,
  html: props => (
    <Box
      dangerouslySetInnerHTML={{ __html: props.node.code }}
      sx={{
        '&': tableStyle
      }}
    />
  ),
  markdown: props => <Markdown {...props} />,
  attribution: props => <Attribution authors={props.node.authors} />,
  callout: props => (
    <Callout type={props.node.calloutType} body={props.node.body[0]} />
  ),
  table: props => <Table rows={props.node.rows} />,
  tabs: props => <Tabs tabs={props.node.tabs} />,
  youtube: ({ node }) => {
    const { url } = node;
    const id = getYouTubeId(url) || undefined;
    return (
      <AspectRatio ratio={16 / 9}>
        <YouTube videoId={id} />
      </AspectRatio>
    );
  },
  arcade: ({ node }) => {
    const { url } = node;
    const demoUrl = url?.replace('app', 'demo').replace('share/', '');
    return (
      <Box
        position="relative"
        paddingBottom="calc(66.66666666666666% + 41px)"
        height="0"
      >
        <iframe
          src={`${demoUrl}?embed`}
          frameBorder={0}
          loading="lazy"
          webkitallowfullscreen={true}
          mozallowfullscreen={true}
          allowFullScreen={true}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
        />
      </Box>
    );
  },
  propsTable: props => <PropsTable props={props.node.propsTable} />
};

const list = props =>
  props.type === 'bullet' ? (
    <UnorderedList>{props.children}</UnorderedList>
  ) : (
    <OrderedList>{props.children}</OrderedList>
  );

const listItem = props => (
  <ListItem lineHeight={1.8}>{props.children}</ListItem>
);

export const marks = {
  code: ({ children }) => (
    <Text
      as="code"
      fontFamily="monospace, monospace"
      fontWeight="medium"
      px={1}
    >
      {children}
    </Text>
  ),
  fragmentId: ({ mark, children }) => {
    const { hash } = mark;
    return <span id={hash}>{children}</span>;
  },
  internalLink: ({ mark, children }) => {
    const { hash, slug = {} } = mark;
    const href = slug?.current;

    if (hash) {
      return (
        <ChakraLink href={`${href || ''}#${hash}`} fontWeight="medium">
          {children}
        </ChakraLink>
      );
    }

    if (href) {
      return (
        <ChakraLink href={href} fontWeight="medium">
          {children}
        </ChakraLink>
      );
    }

    return <span>{children}</span>;
  },
  link: ({ mark, children }) => {
    const { blank, href } = mark;

    if (href) {
      return blank ? (
        <ChakraLink href={href} isExternal fontWeight="medium">
          {children}

          <Icon
            as={ExternalLinkIcon}
            pos="relative"
            top={-0.5}
            ml={0.5}
            boxSize={5}
          />
        </ChakraLink>
      ) : (
        <ChakraLink href={href} fontWeight="medium">
          {children}
        </ChakraLink>
      );
    }

    return <span>{children}</span>;
  }
};

export const serializers = {
  types,
  list,
  listItem,
  marks
};
